/**
 * Booking-duration helper — resolves a human "how long do you book for" unit
 * for a listing so it can be shown consistently across the UI (ListingCard,
 * ListingPage), for every offer type.
 *
 * Most listings expose the unit directly (publicData.unitType = hour/day/week/
 * month/night). `fixed`-unit listings (e.g. Teambüro/Einzelbüro "pro Monat")
 * instead carry the length in publicData.priceVariants[].bookingLengthInMinutes,
 * so we derive the unit from that. `item` (fixdesk) and negotiation offers have
 * no time-based booking → null (no chip).
 */

const MINUTES = { month: 43200, week: 10080, day: 1440, hour: 60 };

const unitFromMinutes = mins => {
  if (!Number.isFinite(mins) || mins <= 0) {
    return null;
  }
  // Pick the largest whole unit the length divides into cleanly, else hour.
  if (mins % MINUTES.month === 0) return 'month';
  if (mins % MINUTES.week === 0) return 'week';
  if (mins % MINUTES.day === 0) return 'day';
  return 'hour';
};

/**
 * @param {Object} publicData listing.attributes.publicData
 * @returns {('hour'|'day'|'night'|'week'|'month')|null} booking-duration unit, or
 *   null when the offer has no time-based booking duration.
 */
export const getBookingDurationUnit = publicData => {
  const { unitType, priceVariants } = publicData || {};

  if (['hour', 'day', 'night', 'week', 'month'].includes(unitType)) {
    return unitType;
  }

  if (unitType === 'fixed') {
    const mins = Array.isArray(priceVariants)
      ? priceVariants.map(v => v?.bookingLengthInMinutes).find(Number.isFinite)
      : null;
    return unitFromMinutes(mins);
  }

  // item, inquiry, offer, request → no time-based booking duration.
  return null;
};

/**
 * Strict unit for PRICE labels.
 *
 * `getBookingDurationUnit` above rounds loosely on purpose — for a "how long can I book for" chip,
 * calling a 2880-minute stay "day" is fine. On a price it is a false claim: the Thusis workation
 * listing sells 2880 minutes (2 working days, 1 night) for CHF 158, and labelling that "ab CHF 158
 * pro Tag" overstates the day rate by 2x — the real one is CHF 79.
 *
 * So this sibling labels a price only when the displayed amount buys EXACTLY one unit. Anything
 * else returns null and the card shows a bare price, which is honest rather than wrong.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @param {number} [priceAmount] listing.attributes.price.amount, in subunits — used to pick the
 *   variant that actually produced the shown price rather than blindly taking the first one.
 * @returns {('hour'|'day'|'night'|'week'|'month')|null}
 */
export const getPriceUnit = (publicData, priceAmount) => {
  const { unitType, priceVariants } = publicData || {};

  if (['hour', 'day', 'night', 'week', 'month'].includes(unitType)) {
    return unitType;
  }

  if (unitType !== 'fixed' || !Array.isArray(priceVariants)) {
    return null;
  }

  const variant = priceVariants.find(v => v?.priceInSubunits === priceAmount) || priceVariants[0];
  const mins = variant?.bookingLengthInMinutes;

  return Object.keys(MINUTES).find(unit => MINUTES[unit] === mins) || null;
};
